import { getServiceUrl } from './api.config';
import { buildProductVersionListFilters } from './search/search.api';
import { ApiClientOptions } from './types/api';
import ApiClient from './utils/ApiClient';
import checkNot from './utils/checkNot';
import qs from './utils/query';
import { isNotEmptyString } from './utils/validators';

type ImportPushPayload = {
  payload_format?: 'json' | 'form';
  importer?: string;
  workflow: 'supplier-directory-v1' | 'rfp-answer-v1';
  organization_id?: number;
};
export default class EtlApi extends ApiClient {
  public constructor(options?: ApiClientOptions) {
    super(getServiceUrl('etl'), options);
  }

  // =============================================================================
  // File Exchange
  // =============================================================================

  public ProductsImportUpload(spider: any, file: any) {
    const query = '/etl/v1/products/import';
    return this.post(query, spider, file);
  }

  public ProductsExportRaw(filters: any) {
    const query = '/etl/v1/products/export';
    const payload: any = {
      ...filters,
      async: true,
    };
    return this.post(query, payload);
  }

  public ProductImportFileSupportStatus(
    productImportFileId: number,
    payload: any,
  ) {
    const query = `/etl/v1/import/support/status/${productImportFileId}`;
    return this.post(query, payload);
  }

  // TODO @alk-aminiailo: remove this endpoint when front-admin switches to `/etl/v2/export/${exportFileId}/references`
  public ProductsExportGtin(exportFileId: number, payload: any) {
    const query = `/etl/v2/export/${exportFileId}/gtins`;
    let parameters = '?alk=1';

    if (checkNot(payload.offset, ['undefined', 'null', 'empty'])) {
      parameters += `&offset=${payload.offset}`;
    }
    if (checkNot(payload.limit, ['undefined', 'null', 'empty'])) {
      parameters += `&limit=${payload.limit}`;
    }
    if (checkNot(payload.filter_gtins_in, ['undefined', 'null', 'empty'])) {
      parameters += `&filter_gtins_in=${payload.filter_gtins_in}`;
    }
    return this.get(query, parameters);
  }

  public ProductsExportReference(exportFileId: number, payload: any) {
    const query = `/etl/v2/export/${exportFileId}/references`;
    let parameters = '?alk=1';

    if (checkNot(payload.offset, ['undefined', 'null', 'empty'])) {
      parameters += `&offset=${payload.offset}`;
    }
    if (checkNot(payload.limit, ['undefined', 'null', 'empty'])) {
      parameters += `&limit=${payload.limit}`;
    }
    if (
      checkNot(payload.filter_references_in, ['undefined', 'null', 'empty'])
    ) {
      parameters += `&filter_references_in=${payload.filter_references_in}`;
    }
    return this.get(query, parameters);
  }

  public ProductsExport(filters: any = {}, asynchronous?: boolean) {
    const query = '/etl/v1/products/export';
    const payload: any = buildProductVersionListFilters({
      queries: filters,
    });
    if (isNotEmptyString(filters.format)) {
      payload.format = filters.format;
    }
    if (isNotEmptyString(filters.sortBy)) {
      payload.sort_by = filters.sortBy;
    }
    if (isNotEmptyString(filters.sortOrder)) {
      payload.sort_order = filters.sortOrder;
    }
    if (filters.columns) {
      payload.with_columns = filters.columns;
    }
    if (filters.organization_id) {
      payload.organization_id = filters.organization_id;
    }
    if (filters.zip_all) {
      payload.zip_all = filters.zip_all;
    }
    if (filters.slice_file_size) {
      payload.slice_file_size = filters.slice_file_size;
    }
    if (filters.use_hierarchy_api) {
      payload.use_hierarchy_api = filters.use_hierarchy_api;
    }
    if (filters.remove_referential_data) {
      payload.remove_referential_data = filters.remove_referential_data;
    }
    if (filters.filter_target_market) {
      payload.filter_target_market = filters.filter_target_market;
    }
    if (filters.target_org_id) {
      payload.target_org_id = filters.target_org_id;
    }
    if (filters.specific_data_org_ids) {
      payload.specific_data_org_ids = filters.specific_data_org_ids;
    }
    if (asynchronous === true) {
      payload.async = true;
      return this.post(query, payload);
    }
    return this.getBlob(query, '', payload, true);
  }

  public DesiredProductsExport() {
    const query = '/etl/v3/desiredproducts/export';
    return this.post(query);
  }

  public PotentialSuppliersExportRaw(filters: any) {
    const query = '/etl/v1/suppliers/potentialsuppliers/export';
    const payload: any = {
      ...filters,
      async: true,
    };
    return this.post(query, payload);
  }

  public ProductImportReportList(filters: any) {
    const query = '/etl/v1/products/import/report';
    let parameters = '?alk=1';
    if (
      checkNot(filters.filter_references_in, ['undefined', 'null', 'empty'])
    ) {
      parameters += `&filter_references_in=${filters.filter_references_in}`;
    }
    if (
      checkNot(filters.filter_step_name_status, ['undefined', 'null', 'empty'])
    ) {
      parameters += `&filter_step_name_status=${filters.filter_step_name_status}`;
    }
    if (
      checkNot(filters.filter_organization_ids, ['undefined', 'null', 'empty'])
    ) {
      parameters += `&filter_organization_ids=${filters.filter_organization_ids}`;
    }
    if (
      checkNot(filters.filter_organization_ids_out, [
        'undefined',
        'null',
        'empty',
      ])
    ) {
      parameters += `&filter_organization_ids_out=${filters.filter_organization_ids_out}`;
    }
    if (checkNot(filters.filter_user_ids_out, ['undefined', 'null', 'empty'])) {
      parameters += `&filter_user_ids_out=${filters.filter_user_ids_out}`;
    }
    if (checkNot(filters.filter_unknown_gln, ['undefined', 'null', 'empty'])) {
      parameters += `&filter_unknown_gln=${filters.filter_unknown_gln}`;
    }
    if (checkNot(filters.filter_empty_out, ['undefined', 'null', 'empty'])) {
      parameters += `&filter_empty_out=${filters.filter_empty_out}`;
    }
    if (checkNot(filters.filter_status, ['undefined', 'null', 'empty'])) {
      parameters += `&filter_status=${filters.filter_status}`;
    }
    if (checkNot(filters.filter_spiders, ['undefined', 'null', 'empty'])) {
      parameters += `&filter_spiders=${filters.filter_spiders}`;
    }
    if (checkNot(filters.filter_date, ['undefined', 'null', 'empty'])) {
      parameters += `&filter_date=${filters.filter_date}`;
    }
    if (checkNot(filters.filter_date_from, ['undefined', 'null', 'empty'])) {
      parameters += `&filter_date_from=${filters.filter_date_from}`;
    }
    if (checkNot(filters.filter_date_to, ['undefined', 'null', 'empty'])) {
      parameters += `&filter_date_to=${filters.filter_date_to}`;
    }
    if (
      checkNot(filters.filter_supportStatus, ['undefined', 'null', 'empty'])
    ) {
      parameters += `&filter_support_status_in=${filters.filter_supportStatus}`;
    }
    if (checkNot(filters.offset, ['undefined', 'null', 'empty'])) {
      parameters += `&offset=${filters.offset}`;
    }
    if (checkNot(filters.limit, ['undefined', 'null', 'empty'])) {
      parameters += `&limit=${filters.limit}`;
    }
    if (checkNot(filters.filter_ids, ['undefined', 'null', 'empty'])) {
      parameters += `&filter_ids=${filters.filter_ids}`;
    }
    return this.get(query, parameters, false);
  }

  public ProductImportReportGet(id: number, filters: any) {
    const query = `/etl/v1/products/import/report/${id}`;
    const qFilters = filters || {};

    let parameters = `?alk=1`;
    if (checkNot(qFilters.filter_error_only, ['undefined', 'null', 'empty'])) {
      parameters += `&filter_error_only=${qFilters.filter_error_only}`;
    }
    if (
      checkNot(qFilters.filter_references_in, ['undefined', 'null', 'empty'])
    ) {
      parameters += `&filter_references_in=${qFilters.filter_references_in}`;
    }
    if (checkNot(qFilters.offset, ['undefined', 'null', 'empty'])) {
      parameters += `&offset=${qFilters.offset}`;
    }
    if (checkNot(qFilters.limit, ['undefined', 'null', 'empty'])) {
      parameters += `&limit=${qFilters.limit}`;
    }
    return this.get(query, parameters, false);
  }

  public ProductImportErrorsReportGet(id: number, filters: any) {
    const query = `/etl/v3/import/product/report/${id}`;
    const qFilters = filters || {};

    let parameters = `?alk=1`;
    if (checkNot(qFilters.offset, ['undefined', 'null', 'empty'])) {
      parameters += `&offset=${qFilters.offset}`;
    }
    if (checkNot(qFilters.limit, ['undefined', 'null', 'empty'])) {
      parameters += `&limit=${qFilters.limit}`;
    }
    return this.get(query, parameters, false);
  }

  public ProductImportReportGetFile(id: number, index: any) {
    const query = `/etl/v1/products/import/${id}/file`;
    let parameters = `?alk=1`;
    if (checkNot(index, ['undefined', 'null', 'empty'])) {
      parameters += `&index=${index}`;
    }
    return this.getBlob(query, parameters);
  }

  public ProductImportErrorsGetXLSX(id: number) {
    const query = `/etl/v3/import/product/errors/${id}/xlsx`;
    return this.getBlob(query, '?alk=1');
  }

  public ProductImportErrorsGetReport(id: number) {
    const query = `/etl/v3/import/product/errors/${id}/report`;
    return this.getBlob(query, '?alk=1');
  }

  public ProductImportFilters() {
    const query = `/etl/v1/products/import/filters`;
    const parameters = `?alk=1`;
    return this.get(query, parameters);
  }

  public ProductImportPush(data: any) {
    const query = `/etl/v2/import/product/push?alk=1`;
    return this.post(query, data);
  }

  public ProductsImportUploadV2(payload: any, file: any) {
    const query = '/etl/v2/import/product/web';
    return this.post(query, payload, file);
  }

  public ProductsImportTemplate(payload: any) {
    const query = '/etl/v2/import/template/download';
    return this.getBlob(query, '', payload, true);
  }

  public ProductsImportTemplateV2(payload: any) {
    const query = '/etl/v3/import/template/download';
    return this.getBlob(query, '', payload, true);
  }

  public LeclercTariffTemplate(queries: any) {
    const query = '/etl/v2/import/template/tariff/download';
    return this.getBlob(query, qs.stringify(queries, true));
  }

  public ProductsImportTemplateFields(
    with_kinds?: boolean,
    with_recipients?: boolean,
  ) {
    const query = '/etl/v2/import/template/fields';
    let parameters = `?alk=1`;
    if (with_kinds === true) {
      parameters += `&with_kinds=1`;
    }
    if (with_recipients === true) {
      parameters += `&with_recipients=1`;
    }
    return this.get(query, parameters);
  }

  public TargetCatalogUpload(payload: any, file: any) {
    const query = '/etl/v1/import/productinshop';
    return this.post(query, payload, file);
  }

  public ImportRangeUpload(payload: any, file: any) {
    const query = '/etl/v2/import/range/web';
    return this.post(query, payload, file);
  }

  public ImportFileStatus(id: any, payload: any) {
    const query = `/etl/v2/import/product/close/${id}`;
    return this.post(query, payload);
  }

  public ExportedFileDownload(
    id: number,
    fileIndex: number,
    stepName: string,
    stepIndex: number,
  ) {
    const query = `/etl/v2/export/${id}/file`;
    let parameters = `?alk=1`;
    if (checkNot(fileIndex, ['undefined', 'null', 'empty'])) {
      parameters += `&file_index=${fileIndex}`;
    }
    if (checkNot(stepName, ['undefined', 'null', 'empty'])) {
      parameters += `&step_name=${stepName}`;
    }
    if (checkNot(stepIndex, ['undefined', 'null', 'empty'])) {
      parameters += `&step_index=${stepIndex}`;
    }
    return this.getBlob(query, parameters);
  }

  public ExportFileFilters() {
    const query = '/etl/v2/export/list/filters';
    return this.get(query, `?alk=1`);
  }

  public ExportFile(filters: any) {
    const query = '/etl/v2/export/list';
    let parameters = `?alk=1`;

    if (
      checkNot(filters.filter_references_in, ['undefined', 'null', 'empty'])
    ) {
      parameters += `&filter_references_in=${filters.filter_references_in}`;
    }
    if (checkNot(filters.filter_gtins_in, ['undefined', 'null', 'empty'])) {
      parameters += `&filter_gtins_in=${filters.filter_gtins_in}`;
    }
    if (checkNot(filters.filter_ids_in, ['undefined', 'null', 'empty'])) {
      parameters += `&filter_ids_in=${filters.filter_ids_in}`;
    }
    if (
      checkNot(filters.filter_organization_id_in, [
        'undefined',
        'null',
        'empty',
      ])
    ) {
      parameters += `&filter_organization_id_in=${filters.filter_organization_id_in}`;
    }
    if (checkNot(filters.filter_format, ['undefined', 'null', 'empty'])) {
      parameters += `&filter_format=${filters.filter_format}`;
    }
    if (
      checkNot(filters.filter_recipient_organization_id, [
        'undefined',
        'null',
        'empty',
      ])
    ) {
      parameters += `&filter_recipient_organization_id=${filters.filter_recipient_organization_id}`;
    }
    if (
      checkNot(filters.filter_source_organization_id, [
        'undefined',
        'null',
        'empty',
      ])
    ) {
      parameters += `&filter_source_organization_id=${filters.filter_source_organization_id}`;
    }

    if (checkNot(filters.filter_step_name, ['undefined', 'null', 'empty'])) {
      parameters += `&filter_step_name=${filters.filter_step_name}`;
    }

    if (checkNot(filters.filter_step_status, ['undefined', 'null', 'empty'])) {
      parameters += `&filter_step_status=${filters.filter_step_status}`;
    }

    if (checkNot(filters.offset, ['undefined', 'null', 'empty'])) {
      parameters += `&offset=${filters.offset}`;
    }
    if (checkNot(filters.limit, ['undefined', 'null', 'empty'])) {
      parameters += `&limit=${filters.limit}`;
    }
    if (checkNot(filters.with_gtins, ['undefined', 'null', 'empty'])) {
      parameters += `&with_gtins=${filters.with_gtins}`;
    }
    if (filters.start_date) {
      parameters += `&start_date=${filters.start_date}`;
    }
    if (filters.end_date) {
      parameters += `&end_date=${filters.end_date}`;
    }
    return this.get(query, parameters);
  }

  public ExportSynchronizationStatus(data: any) {
    const query = `/etl/v2/export/sync/status`;
    return this.post(query, data);
  }

  public ProductImportReportGenerate(data: any) {
    const query = `/etl/v1/products/import/report/generate`;
    return this.getBlob(query, '', data, true);
  }

  public QueuePusher(route: string, synchronous = false, payload: any) {
    const query = `/etl/v1/pusher/push`;
    return this.post(query, {
      route,
      synchronous,
      payload,
    });
  }
  public GetStatistics(filters: any) {
    const query = `/etl/v1/stats`;
    return this.post(query, filters);
  }

  public CancelExport(exportFileIds: number[], isCanceled: boolean) {
    const query = `/etl/v2/export/cancel`;
    return this.post(query, {
      export_file_ids: exportFileIds,
      is_canceled: isCanceled,
    });
  }

  public ImportList(filters: any) {
    const query = `/etl/v2/tracker/import/list`;
    return this.post(query, filters);
  }

  public ImportGet(id: number) {
    const query = `/etl/v2/tracker/import/${id}`;
    return this.get(query, '', false);
  }

  public ImportEntities(id: number, filters: any) {
    const query = `/etl/v2/tracker/import/${id}/entities`;
    return this.post(query, filters);
  }

  public ImportGetFile(id: number, index: any) {
    const query = `/etl/v2/tracker/import/${id}/file`;
    let parameters = `?alk=1`;
    if (checkNot(index, ['undefined', 'null', 'empty'])) {
      parameters += `&index=${index}`;
    }
    return this.getBlob(query, parameters);
  }

  public ImportFilters() {
    const query = `/etl/v2/tracker/import/filters`;
    return this.get(query, '', false);
  }

  public ImportPush(payload: ImportPushPayload, file: File) {
    const query = '/etl/v2/tracker/import/push';
    return this.post(query, payload, file);
  }

  public ImportProductReport(offset: number, limit: number) {
    const query = '/etl/v3/import/product';
    let parameters = `?alk=1`;
    if (checkNot(offset, ['undefined', 'null', 'empty'])) {
      parameters += `&offset=${offset}`;
    }
    if (checkNot(limit, ['undefined', 'null', 'empty'])) {
      parameters += `&limit=${limit}`;
    }
    return this.get(query, parameters);
  }
}
